const CHAIR_SIZE = 14;
const GAP = 6;
const CIRCLE_GAP = 2;

function circleChairPositions(width, height, seatCount) {
  const r = width / 2 + CIRCLE_GAP + CHAIR_SIZE / 2;
  const cx = width / 2;
  const cy = height / 2;
  const chairs = [];

  for (let i = 0; i < seatCount; i++) {
    const angle = (360 / seatCount) * i;
    const theta = (angle * Math.PI) / 180;
    chairs.push({
      x: cx + r * Math.sin(theta),
      y: cy - r * Math.cos(theta),
      rotation: angle,
    });
  }

  return chairs;
}

// Walking the perimeter as one continuous loop (the previous approach) evenly spaces seats by
// arc-length overall, but that doesn't mean each side ends up symmetric about its own center, or
// that opposite sides match - the split point wanders depending on seatCount vs. the corners it
// happens to fall between. Instead: split seatCount into two side-PAIRS - top+bottom and
// left+right - proportional to side length, always giving each pair's two sides equal counts, so
// the layout is mirror-symmetric front<->back and left<->right; then place each side's seats at
// evenly spaced interior division points, which is automatically symmetric about that side's own
// midpoint. Works for squares too (width === height is just a special case, split 50/50).
function rectangleChairPositions(width, height, seatCount) {
  const half = CHAIR_SIZE / 2 + GAP;
  const chairs = [];

  const pairUnits = Math.floor(seatCount / 2);
  const hasOddSeat = seatCount % 2 === 1;

  const hUnits = pairUnits === 0 ? 0 : Math.round((pairUnits * width) / (width + height));
  const vUnits = pairUnits - hUnits;

  const placeOnSide = (count, sideLength, mapToXY) => {
    for (let i = 1; i <= count; i++) {
      const d = (sideLength * i) / (count + 1);
      chairs.push(mapToXY(d));
    }
  };

  placeOnSide(hUnits, width, (d) => ({ x: d, y: -half, rotation: 180 }));
  placeOnSide(vUnits, height, (d) => ({ x: width + half, y: d, rotation: 270 }));
  placeOnSide(hUnits, width, (d) => ({ x: d, y: height + half, rotation: 0 }));
  placeOnSide(vUnits, height, (d) => ({ x: -half, y: d, rotation: 90 }));

  // An odd seat count can't split into perfectly mirrored pairs - the one leftover seat goes at
  // the midpoint of whichever side is longest, the least awkward place for a single unmatched seat.
  if (hasOddSeat) {
    if (width >= height) {
      chairs.push({ x: width / 2, y: height + half, rotation: 0 });
    } else {
      chairs.push({ x: width + half, y: height / 2, rotation: 270 });
    }
  }

  return chairs;
}

/**
 * Computes chair marker positions around a table's perimeter, purely from its shape/size/seat count.
 * Coordinates are in the table's own unrotated local space (origin top-left), so the caller can render
 * chairs alongside the table shape inside the same rotated wrapper and have them rotate together.
 */
export function getChairPositions({ shape, width, height, seatCount }) {
  const count = Number(seatCount) || 0;
  if (count <= 0 || !width || !height) {
    return [];
  }

  return shape === 'circle'
    ? circleChairPositions(width, height, count)
    : rectangleChairPositions(width, height, count);
}
